import { API_ENDPOINTS, API_CONFIG } from '@/constants'
import { queryLocalCv } from '@/services/localCv'

export type AIProvider = 'aifly' | 'chatgpt' | 'local'

interface AIResponse {
  response: string
  provider: AIProvider
  citations?: { sources: Array<{ document: string; pages?: string[]; sections?: string[] }> } | null
}

interface CacheEntry {
  response: string
  timestamp: number
  provider: AIProvider
}

class AIService {
  private responseCache = new Map<string, CacheEntry>()
  private lastRequestTime = 0
  private conversationId: string | undefined

  async sendMessage(message: string): Promise<AIResponse> {
    const cacheKey = this.generateCacheKey(message)
    const cached = this.getCachedResponse(cacheKey)
    if (cached) {
      return { response: cached.response, provider: cached.provider }
    }

    await this.enforceRateLimit()

    // OpenAI is the configured primary provider. Keep Aifly as a secondary
    // provider so it can resume automatically if its project is restored.
    try {
      const response = await this.queryChatGPT(message)
      this.cacheResponse(cacheKey, response, 'chatgpt')
      return { response, provider: 'chatgpt' }
    } catch (chatgptError) {
      console.warn('ChatGPT API failed, falling back to Aifly:', chatgptError)
      try {
        const result = await this.queryAifly(message)
        this.cacheResponse(cacheKey, result.response, 'aifly')
        return { response: result.response, provider: 'aifly', citations: result.citations }
      } catch (aiflyError) {
        console.error('Aifly fallback also failed:', aiflyError)
        const response = queryLocalCv(message)
        return { response, provider: 'local' }
      }
    }
  }

  private async queryAifly(
    message: string
  ): Promise<{ response: string; citations?: AIResponse['citations'] }> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 60000)

    try {
      const body: Record<string, string> = { message }
      if (this.conversationId) {
        body.conversation_id = this.conversationId
      }

      const response = await fetch(API_ENDPOINTS.CHAT_AIFLY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`Aifly HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.error) {
        throw new Error(data.error.message || data.error)
      }

      if (data.conversation_id) {
        this.conversationId = data.conversation_id
      }

      const text = data.response
      if (!text) {
        throw new Error('Empty response from Aifly')
      }

      return { response: text, citations: data.citations }
    } catch (error) {
      clearTimeout(timeoutId)
      throw error
    }
  }

  private async queryChatGPT(message: string): Promise<string> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT)

    try {
      const response = await fetch(API_ENDPOINTS.CHAT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.error) {
        if (data.details?.fix) {
          throw new Error(
            `${data.error}: ${data.message || ''}\n\n${data.details.fix}`
          )
        }
        throw new Error(
          data.error + (data.message ? `: ${data.message}` : '')
        )
      }

      const text = data.response || data.message
      if (!text) {
        throw new Error('Empty response from ChatGPT')
      }

      return text
    } catch (error) {
      clearTimeout(timeoutId)
      throw error
    }
  }

  private generateCacheKey(message: string): string {
    return encodeURIComponent(message.toLowerCase().trim())
  }

  private getCachedResponse(cacheKey: string): CacheEntry | null {
    const cached = this.responseCache.get(cacheKey)
    if (!cached) return null

    if (Date.now() - cached.timestamp > API_CONFIG.CACHE_DURATION) {
      this.responseCache.delete(cacheKey)
      return null
    }

    return cached
  }

  private cacheResponse(
    cacheKey: string,
    response: string,
    provider: AIProvider
  ): void {
    this.responseCache.set(cacheKey, {
      response,
      timestamp: Date.now(),
      provider,
    })
  }

  private async enforceRateLimit(): Promise<void> {
    const timeSinceLastRequest = Date.now() - this.lastRequestTime
    if (timeSinceLastRequest < 1000) {
      await new Promise(resolve =>
        setTimeout(resolve, 1000 - timeSinceLastRequest)
      )
    }
    this.lastRequestTime = Date.now()
  }

  clearCache(): void {
    this.responseCache.clear()
    this.conversationId = undefined
  }
}

export const aiService = new AIService()
